import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Table,
    message,
} from 'antd';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import { PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';

const emptyPage: PageResult<any> = { items: [], pageNo: 1, pageSize: 20, total: 0 };
const SignalsPage = () => {
    const [form] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({
        pageNo: 1,
        pageSize: 20,
        status: 'PENDING_ADMIN',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [current, setCurrent] = useState<any>(null);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.signals(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);
    const triage = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            await v2Api.triageSignal(current.id, {
                ...values,
                version: current.version,
                dueAt: values.dueAt ? new Date(values.dueAt).toISOString() : undefined,
            });
            message.success('信号已分诊');
            setCurrent(null);
            form.resetFields();
            load();
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };
    const columns = [
        { title: '信号类型', dataIndex: 'signalType', width: 140 },
        {
            title: '企业',
            dataIndex: 'companyName',
            width: 190,
            render: (v: string, row: any) => v || `企业 #${row.companyId}`,
        },
        {
            title: '信号摘要',
            dataIndex: 'summary',
            render: (v: string, row: any) => (
                <div>
                    <strong>{v || row.subject || '待判断客户信号'}</strong>
                    <div className="v2-muted">来源：{row.sourceType || '系统事件'}</div>
                </div>
            ),
        },
        {
            title: '要求处理时间',
            dataIndex: 'requiredActionAt',
            width: 170,
            render: formatDateTime,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, row: any) => (
                <PermissionGuard anyOf={['SIGNAL_TRIAGE']}>
                    <Button
                        type="link"
                        disabled={row.status !== 'PENDING_ADMIN'}
                        onClick={() => {
                            setCurrent(row);
                            form.setFieldsValue({
                                triageResult: 'VALID',
                                processingAction: 'CREATE_TASK',
                            });
                        }}
                    >
                        分诊
                    </Button>
                </PermissionGuard>
            ),
        },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="开发与培育 · 客户事实"
                title="客户信号"
                description="客户回复与行为信号只用于人工分诊；不会自动推进商机、项目或开发周期状态。"
            />
            <Alert
                type="warning"
                showIcon
                message="发送批次和邮件模板已迁移到独立管理页面"
                description="本页继续负责信号查询与人工分诊；开发周期、培育计划以及自动收取回复的服务端 Worker 仍待接入。"
            />
            <Card className="v2-filter-card">
                <Space>
                    <Select
                        allowClear
                        value={filters.status}
                        style={{ width: 150 }}
                        onChange={(status) => setFilters({ ...filters, status, pageNo: 1 })}
                    >
                        <Select.Option value="PENDING_ADMIN">待分诊</Select.Option>
                        <Select.Option value="PROCESSING">处理中</Select.Option>
                        <Select.Option value="CLOSED">已关闭</Select.Option>
                    </Select>
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 900 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
            <Modal
                title="人工分诊客户信号"
                visible={!!current}
                onOk={triage}
                confirmLoading={saving}
                onCancel={() => setCurrent(null)}
                okText="确认分诊"
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="判断结果" name="triageResult" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="VALID">有效信号</Select.Option>
                            <Select.Option value="IRRELEVANT">无关信号</Select.Option>
                            <Select.Option value="DUPLICATE">重复信号</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="处理动作"
                        name="processingAction"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="CREATE_TASK">创建任务</Select.Option>
                            <Select.Option value="CLAIM">认领处理</Select.Option>
                            <Select.Option value="CLOSE">仅记录并关闭</Select.Option>
                            <Select.Option value="RETURN_NURTURE" disabled>
                                返回培育（服务端待接入）
                            </Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="判断依据"
                        name="reason"
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) =>
                            prev.processingAction !== next.processingAction
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('processingAction') === 'CREATE_TASK' ? (
                                <>
                                    <Form.Item
                                        label="任务标题"
                                        name="taskTitle"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item label="负责人 ID" name="assigneeId">
                                        <InputNumber min={1} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item label="截止时间" name="dueAt">
                                        <Input type="datetime-local" />
                                    </Form.Item>
                                </>
                            ) : null
                        }
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
export default SignalsPage;
